'use client';

import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { motion } from 'framer-motion';
import { Zap, Info } from 'lucide-react';

interface PieChartMobileProps {
  data: Array<{ name: string; value: number; color: string }>;
  title?: string;
  centerLabel?: string;
  centerValue?: string;
  showLegend?: boolean;
  animate?: boolean;
}

const PieChartMobile: React.FC<PieChartMobileProps> = ({
  data,
  title,
  centerLabel,
  centerValue,
  showLegend = true,
  animate = true,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const handlePieEnter = (_: any, index: number) => {
    setActiveIndex(index);
  };

  const handlePieLeave = () => {
    setActiveIndex(null);
  };

  const total = data.reduce((sum, entry) => sum + entry.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const percentage = ((data.value / total) * 100).toFixed(1);
      
      return (
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <p className="font-semibold text-sm" style={{ color: data.payload.color }}>
            {data.name}
          </p>
          <p className="text-xs text-gray-600 mt-1">
            {data.value.toLocaleString()} kWh ({percentage}%)
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = (entry: any) => {
    const percentageValue = (entry.value / total) * 100;
    const percentageText = percentageValue.toFixed(0);
    return percentageValue > 5 ? `${percentageText}%` : '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-2xl shadow-lg p-4"
    >
      {/* Header */}
      {title && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900">{title}</h3>
          </div>
          <Info className="w-4 h-4 text-gray-400" />
        </div>
      )}

      {/* Chart */}
      <div className="relative h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={renderCustomLabel}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              animationBegin={0}
              animationDuration={animate ? 800 : 0}
              onMouseEnter={handlePieEnter}
              onMouseLeave={handlePieLeave}
            >
              {data.map((entry, index) => (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color}
                  style={{
                    filter: activeIndex === index ? 'brightness(1.1)' : 'brightness(1)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                  }}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center Label */}
        {(centerLabel || centerValue) && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              {centerValue && (
                <div className="text-2xl font-bold text-gray-900">{centerValue}</div>
              )}
              {centerLabel && (
                <div className="text-xs text-gray-600 mt-1">{centerLabel}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="mt-4 space-y-2">
          {data.map((entry, index) => {
            const percentage = ((entry.value / total) * 100).toFixed(1);
            const isActive = activeIndex === index;
            
            return (
              <motion.div
                key={entry.name}
                className={`flex items-center justify-between p-2 rounded-lg transition-colors ${
                  isActive ? 'bg-gray-50' : ''
                }`}
                animate={{ scale: isActive ? 1.02 : 1 }}
              >
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-sm text-gray-700">{entry.name}</span>
                </div>
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {entry.value.toLocaleString()} kWh
                  </div>
                  <div className="text-xs text-gray-500">{percentage}%</div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Total */}
      <div className="mt-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-sm text-gray-600">Gesamt</span>
          <span className="text-lg font-bold text-gray-900">
            {total.toLocaleString()} kWh
          </span>
        </div>
      </div>
    </motion.div>
  );
};

export default PieChartMobile;